import { yupResolver } from '@hookform/resolvers/yup'
import DoneIcon from '@mui/icons-material/Done'
import LoopIcon from '@mui/icons-material/Loop'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import { Grid, IconButton } from '@mui/material'
import { useGetValueList } from '@src/data/api/config/config-api'
import { memo, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { InputTextField, PageError } from '../../../components'
import ActionsDialog from '../../../components/ActionsDialog'
import { useBusinessErrors } from '@src/data/selectors/app-config-selectors'
import { BankAccount, BankAccountSchema } from '../../../data/types'
import { reportErrorToConsole } from '../../../services/error-logger'

interface Props {
  bankAccount: BankAccount | undefined
  open: boolean
  onConfirm: (data: BankAccount) => void
  onCancel: () => void
}

const EditBankAccountDialog = ({ bankAccount, onConfirm, onCancel, open }: Props) => {
  const { t } = useTranslation()
  const error = useBusinessErrors()
  const [financialInstitutions, isFetchingFinancialInstitutions] = useGetValueList('FinancialInstitutions')

  const {
    register,
    reset,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<BankAccount>({
    mode: 'onBlur',
    defaultValues: bankAccount,
    resolver: yupResolver(BankAccountSchema),
  })

  const [validInstitution, setValidInstitution] = useState(
    financialInstitutions?.find((item) => item.id === getValues('institutionNumber')) !== undefined,
  )

  useEffect(() => {
    reset(bankAccount)
  }, [bankAccount, reset])

  const onCopy = (e: React.FormEvent) => {
    e.preventDefault()
  }

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const institution = financialInstitutions?.find((f) => f.id === value)
    if (institution) {
      setValue('bankName', institution.text)
      setValidInstitution(true)
    } else {
      setValue('bankName', '')
      setValidInstitution(false)
    }
  }

  return (
    <ActionsDialog
      title={t('bankAccount.title')}
      onCancel={onCancel}
      onConfirm={handleSubmit(onConfirm, reportErrorToConsole)}
      open={open}
    >
      <PageError errors={error} />
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid size={5}>
          <InputTextField
            maxLength={3}
            error={errors?.institutionNumber}
            label={t('bankAccount.institutionNumber')}
            {...register('institutionNumber')}
            onChange={onChange}
          />
        </Grid>
        <Grid size={1}>
          <IconButton disabled>
            {isFetchingFinancialInstitutions || !getValues('institutionNumber') ? (
              <LoopIcon fontSize="large" color="primary" />
            ) : null}
            {getValues('institutionNumber') && !validInstitution ? (
              <ReportProblemIcon fontSize="large" color="warning" />
            ) : null}
            {getValues('institutionNumber') && validInstitution ? <DoneIcon fontSize="large" color="success" /> : null}
          </IconButton>
        </Grid>
        <Grid size={6}>
          <InputTextField
            maxLength={50}
            error={errors?.bankName}
            label={t('bankAccount.bankName')}
            {...register('bankName')}
          />
        </Grid>
        <Grid size={6}>
          <InputTextField
            maxLength={5}
            error={errors?.transitNumber}
            label={t('bankAccount.transitNumber')}
            onCopy={onCopy}
            {...register('transitNumber')}
          />
        </Grid>
        <Grid size={6}>
          <InputTextField
            maxLength={5}
            error={errors?.transitNumberConfirm}
            label={t('bankAccount.transitNumberConfirm')}
            {...register('transitNumberConfirm')}
            onCopy={onCopy}
            onPaste={onCopy}
          />
        </Grid>
        <Grid size={6}>
          <InputTextField
            maxLength={12}
            error={errors?.accountNumber}
            label={t('bankAccount.accountNumber')}
            onCopy={onCopy}
            {...register('accountNumber')}
          />
        </Grid>
        <Grid size={6}>
          <InputTextField
            maxLength={12}
            error={errors?.accountNumberConfirm}
            label={t('bankAccount.accountNumberConfirm')}
            {...register('accountNumberConfirm')}
            onCopy={onCopy}
            onPaste={onCopy}
          />
        </Grid>
      </Grid>
    </ActionsDialog>
  )
}
export default memo(EditBankAccountDialog)
